import { useAuth0, User, withAuthenticationRequired } from "@auth0/auth0-react";
import React, {
  MutableRefObject,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useQueryClient } from "react-query";
import { buildRequestApi, RequestApi } from "./api";
import {
  ApplicationCache,
  CacheKeys,
  ApplicationCacheReturnTypes,
  useCache,
} from "./api/Cache";
import { ApplicationPermissions } from "./api/Users";
import Loading from "./shared-components/Loading";
import Skeleton from "./shared-components/Skeleton";
import { ApplicationToastProvider } from "./webcontent/ApplicationToastProvider";
import { initializeLogging } from "./utils";
import { useReportLockEvents } from "./hooks/useReportLockEvents";
import { WebSocketProvider } from "./services/websocket/WebSocketContext";
import GlobalNotificationHandler from "./shared-components/GlobalNotificationHandler";

function ReportLockEventsHandler() {
  useReportLockEvents();
  return null;
}

export interface CompaniesFromAuth {
  companyIds?: string[];
  allCompanies?: boolean;
}

type AppContextProps = {
  user?: User;
  permissions?: ApplicationPermissions[];
  roles: string[];
  companiesFromAuth: CompaniesFromAuth;
  requestApi: RequestApi;
  cache: MutableRefObject<ApplicationCache>;
  hasTask?: boolean;
  messageLog: any[];
  logMessage: (message: any, details?: any) => void;
  refreshCache: (
    key: CacheKeys,
    value?: ApplicationCacheReturnTypes,
    page?: number,
    type?: string,
    search?: string
  ) => void;
  resetAccessToken: () => Promise<string>;
};

const ApiContext = React.createContext<AppContextProps | undefined>(undefined);

function Provider({ children }: { children: any }) {
  const { user, getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();
  const prevAuth0SubRef = useRef<string | undefined>();
  const [requestApi, setRequestApi] = useState<RequestApi>();
  // Held in a ref, not state: the log is only read on demand by Nav's debug download, so appending
  // to it must not re-render every context consumer.
  const errorsRef = useRef<any[]>([]);
  const [permissions, setPermissions] = useState<ApplicationPermissions[]>([]);
  const [roles, setRoles] = useState<string[]>([]);
  const [companiesFromAuth, setCompaniesFromAuth] = useState<CompaniesFromAuth>(
    {}
  );
  const { refreshCache, cacheRef } = useCache();
  useEffect(() => {
    getAccessTokenSilently().then((accessToken) => {
      const _requestsApi = buildRequestApi(accessToken);
      setRequestApi(_requestsApi);
    });
  }, [getAccessTokenSilently]);

  useEffect(() => {
    const keys = Object.keys(user || {});

    const roleKey = keys.find((key) => key.includes("roles"));
    const permissionsKey = keys.find((key) => key.includes("permissions"));
    const companiesFromAuthKey = keys.filter((key) =>
      key.includes("companyInfo")
    );
    let _permissions: ApplicationPermissions[] = [];
    let _roles: string[] = [];

    if (roleKey) {
      _roles = user?.[roleKey] || [];
    }

    if (permissionsKey) {
      _permissions = user?.[permissionsKey] || [];
    }

    if (companiesFromAuthKey.length > 0) {
      const companiesFromAuth = user?.[companiesFromAuthKey[0]] || {};
      setCompaniesFromAuth(companiesFromAuth);
    }

    setPermissions(_permissions);
    setRoles(_roles);
  }, [user]);

  useEffect(() => {
    const sub = user?.sub;
    const authUserChanged =
      prevAuth0SubRef.current !== undefined && prevAuth0SubRef.current !== sub;

    if (authUserChanged) {
      void queryClient.invalidateQueries("feature-flags-bulk");
    }

    if (authUserChanged && sub !== undefined) {
      void queryClient.invalidateQueries(["actorInfo"]);
    }
    if (sub !== undefined) {
      prevAuth0SubRef.current = sub;
    }
  }, [queryClient, user?.sub]);

  const logMessage = useCallback((message: string, details?: any) => {
    errorsRef.current.push({
      title: message,
      message: details,
      date: new Date().toLocaleString(),
    });
  }, []);

  useEffect(() => {
    initializeLogging(logMessage);
  }, [logMessage]);

  const resetAccessToken = useCallback(async () => {
    const newToken = await getAccessTokenSilently({ ignoreCache: true });
    const _requestsApi = buildRequestApi(newToken);
    setRequestApi(_requestsApi);
    return newToken;
  }, [getAccessTokenSilently]);

  // Memoized so unrelated Provider re-renders do not hand every consumer a new context object and
  // re-render the whole app, which can discard in-flight request state (e.g. the 360 workbench's
  // sub-workflow fetch, which gates the "Complete Search" button).
  const contextValue = useMemo(
    () => ({
      permissions,
      roles,
      companiesFromAuth,
      user,
      requestApi: requestApi as RequestApi,
      cache: cacheRef,
      refreshCache,
      logMessage,
      messageLog: errorsRef.current,
      resetAccessToken,
    }),
    [
      permissions,
      roles,
      companiesFromAuth,
      user,
      requestApi,
      cacheRef,
      refreshCache,
      logMessage,
      resetAccessToken,
    ]
  );

  if (!requestApi) return <Loading />;
  return (
    <ApiContext.Provider value={contextValue}>
      <WebSocketProvider>
        <ReportLockEventsHandler />
        <GlobalNotificationHandler />
        <ApplicationToastProvider>{children}</ApplicationToastProvider>
      </WebSocketProvider>
    </ApiContext.Provider>
  );
}

export const AppProvider = withAuthenticationRequired(Provider, {
  onRedirecting: () => <Skeleton />,
});

export function useAppContext() {
  const props = useContext(ApiContext) as AppContextProps;

  return {
    permissions: props?.permissions,
    user: props?.user,
    roles: props?.roles,
    requestApi: props?.requestApi,
    cache: props?.cache,
    messageLog: props?.messageLog,
    companiesFromAuth: props?.companiesFromAuth,
    hasTask: props?.hasTask,
    logMessage: props?.logMessage,
    refreshCache: props?.refreshCache,
    resetAccessToken: props?.resetAccessToken,
  };
}
